<template>
  <div class="signup-view">
    <!-- Sign-up header -->
    <div class="auth-welcome">
      <p class="growth-eyebrow">
        {{ $t('refresh.auth-kicker') }}
      </p>
      <h1 class="welcome-title">
        {{ $t('refresh.signup-title') }}
      </h1>
      <p class="welcome-subtitle">
        {{ $t('refresh.signup-description') }}
      </p>
    </div>

    <form @submit.prevent="submitSignup">
      <!-- Personal details section -->
      <div class="form-divider mb-4">
        <div class="divider-line" />
        <span class="divider-text">{{ $t('auth.personal-info') }}</span>
        <div class="divider-line" />
      </div>

      <div class="columns auth-name-row">
        <div class="column">
          <div class="indiefy-field">
            <BaseInput
              v-model="firstName"
              :label="$t('auth.first-name')"
              :placeholder="$t('auth.first-name')"
              type="text"
              data-test="first-name-input"
              autocomplete="given-name"
              :has-error="!!formErrors.firstName"
              :error-message="formErrors.firstName"
              required
              @blur="validateFirstName"
            />
          </div>
        </div>

        <div class="column">
          <div class="indiefy-field">
            <BaseInput
              v-model="lastName"
              :label="$t('auth.last-name')"
              :placeholder="$t('auth.last-name')"
              type="text"
              data-test="last-name-input"
              autocomplete="family-name"
              :has-error="!!formErrors.lastName"
              :error-message="formErrors.lastName"
              required
              @blur="validateLastName"
            />
          </div>
        </div>
      </div>

      <div class="indiefy-field">
        <BaseInput
          v-model="email"
          :label="$t('auth.email')"
          :placeholder="$t('auth.email')"
          type="email"
          data-test="email-input"
          :has-error="!!formErrors.email"
          :error-message="formErrors.email"
          required
          autocomplete="email"
          @blur="validateEmail"
        />
      </div>

      <div class="columns is-mobile auth-phone-row">
        <div class="column is-4-desktop is-5-tablet is-5-mobile">
          <div class="indiefy-field">
            <div class="country-code-dropdown">
              <label class="label">{{ $t('refresh.calling-code') }}</label>
              <select
                v-model="phoneCode"
                class="country-select"
                :aria-label="$t('refresh.calling-code')"
                required
                data-test="phone-code-input"
                @blur="validatePhoneCode"
              >
                <option
                  v-for="country in countryCodes"
                  :key="country.code"
                  :value="country.code"
                >
                  {{ country.flag }} {{ country.name }} ({{ country.code }})
                </option>
              </select>
            </div>
            <p
              v-if="formErrors.phoneCode"
              class="help is-danger mt-1"
            >
              {{ formErrors.phoneCode }}
            </p>
          </div>
        </div>

        <div class="column">
          <div class="indiefy-field">
            <BaseInput
              v-model="phoneNumber"
              :label="$t('auth.phone-number')"
              :placeholder="$t('auth.phone-number')"
              type="tel"
              data-test="phone-number-input"
              autocomplete="tel-national"
              :has-error="!!formErrors.phoneNumber"
              :error-message="formErrors.phoneNumber"
              required
              @blur="validatePhoneNumber"
            />
          </div>
        </div>
      </div>

      <!-- Security section -->
      <div class="form-divider mb-4 mt-5">
        <div class="divider-line" />
        <span class="divider-text">{{ $t('auth.security') }}</span>
        <div class="divider-line" />
      </div>

      <div class="indiefy-field">
        <BaseInput
          v-model="password"
          :label="$t('auth.password')"
          :placeholder="$t('auth.password')"
          type="password"
          data-test="password-input"
          :has-error="!!formErrors.password"
          :error-message="formErrors.password"
          required
          autocomplete="new-password"
          @blur="validatePassword"
        />
        <div
          v-if="password"
          class="password-strength mt-2"
        >
          <div class="strength-meter">
            <div
              class="strength-value"
              :style="{ width: passwordStrength + '%' }"
              :class="passwordStrengthClass"
            />
          </div>
          <span
            class="strength-text is-size-7"
            :class="'has-text-' + passwordStrengthClass"
          >
            {{ passwordStrengthText }}
          </span>
        </div>
      </div>

      <div class="indiefy-field">
        <BaseInput
          v-model="passwordConfirm"
          :label="$t('auth.confirm-password')"
          :placeholder="$t('auth.confirm-password')"
          type="password"
          data-test="confirm-password-input"
          :has-error="!!formErrors.passwordConfirm"
          :error-message="formErrors.passwordConfirm"
          required
          autocomplete="new-password"
          @blur="validatePasswordConfirm"
        />
      </div>

      <!-- Terms and Agreement -->
      <div class="indiefy-field mt-5">
        <label class="terms-checkbox">
          <span class="custom-checkbox">
            <input
              v-model="agreeToTerms"
              type="checkbox"
              required
              @change="validateTerms"
            >
          </span>
          <span class="terms-text">
            {{ $t('auth.agree-to-terms') }}
            <a
              href="/terms-and-conditions"
              target="_blank"
              rel="noopener noreferrer"
              class="terms-link"
            >{{ $t('auth.terms-and-conditions') }}</a>
          </span>
        </label>
        <p
          v-if="formErrors.terms"
          class="help is-danger mt-1"
        >
          {{ formErrors.terms }}
        </p>
      </div>

      <div class="mt-5 has-text-centered">
        <BaseButton
          type="submit"
          variant="primary"
          is-fullwidth
          :is-loading="isLoading"
          data-test="signup-button"
          is-animated
        >
          {{ $t('auth.create-account') }}
        </BaseButton>
      </div>

      <div
        v-if="authError"
        class="error-container mt-4 p-3"
      >
        <i class="fas fa-exclamation-circle mr-2" />
        {{ authError }}
      </div>
    </form>

    <div class="form-divider mb-4 mt-5">
      <div class="divider-line" />
      <span class="divider-text">{{ $t('auth.or-continue-with') }}</span>
      <div class="divider-line" />
    </div>

    <div class="social-signup-buttons mb-4 is-flex">
      <button
        class="social-button google"
        :disabled="isLoading"
        @click="loginWithGoogle"
      >
        <i class="fab fa-google" />
        <span>Google</span>
      </button>
      <button
        class="social-button apple"
        :disabled="isLoading"
        @click="loginWithApple"
      >
        <i class="fab fa-apple" />
        <span>Apple</span>
      </button>
    </div>

    <div class="mt-4 has-text-centered">
      <p class="mb-3">
        {{ $t('auth.already-account') }}
      </p>
      <router-link
        :to="{ name: 'login', query: { redirect: route.query.redirect } }"
        class="sign-in-link"
      >
        {{ $t('auth.sign-in') }}
      </router-link>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watchEffect, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useAuthStore } from '@/store/auth';
import BaseInput from '@/components/ui/BaseInput.vue';
import BaseButton from '@/components/ui/BaseButton.vue';
import { useI18n } from 'vue-i18n';
import countries from '@/data/countries.js';
import publicService from '@/services/publicService.js';
import { usePageSeo } from '@/composables/usePageSeo';
import { internalAuthDestination } from '@/utils/acquisition';

usePageSeo({
  titleKey: 'auth.signup-title',
  descriptionKey: 'auth.signup-description',
});

// Router
const router = useRouter();
const route = useRoute();
const signupDestination = () => internalAuthDestination(route.query.redirect);

// Auth store
const authStore = useAuthStore();
const isLoading = computed(() => authStore.isLoading);
const authError = computed(() => authStore.authError);

// Public service
const locationSettings = ref(null);

// i18n
const { t } = useI18n();

// Only use countries with phone_code and flag
const countryCodes = countries
  .filter(c => c.phone_code && c.flag)
  .map(c => ({
    code: c.phone_code,
    flag: c.flag,
    name: c.name,
    iso: c.code,
  }))
  .sort((a, b) => a.name.localeCompare(b.name));

// Form state
const firstName = ref('');
const lastName = ref('');
const email = ref(window.__indiefyCheckoutEmail || '');
delete window.__indiefyCheckoutEmail;
const phoneCode = ref('+1');
const phoneNumber = ref('');
const password = ref('');
const passwordConfirm = ref('');
const agreeToTerms = ref(false);
const formErrors = ref({
  firstName: '',
  lastName: '',
  email: '',
  phoneCode: '',
  phoneNumber: '',
  password: '',
  passwordConfirm: '',
  terms: '',
});

// Password strength
const passwordStrength = ref(0);
const passwordStrengthClass = ref('danger');
const passwordStrengthText = ref('');

// Calculate password strength
watchEffect(() => {
  if (!password.value) {
    passwordStrength.value = 0;
    passwordStrengthClass.value = 'danger';
    passwordStrengthText.value = '';
    return;
  }

  let strength = 0;
  // Length check
  if (password.value.length >= 8) strength += 25;
  if (password.value.length >= 12) strength += 15;

  // Character type checks
  if (/[0-9]/.test(password.value)) strength += 15;
  if (/[a-z]/.test(password.value)) strength += 15;
  if (/[A-Z]/.test(password.value)) strength += 15;
  if (/[^a-zA-Z0-9]/.test(password.value)) strength += 15;

  passwordStrength.value = Math.min(strength, 100);

  if (passwordStrength.value < 30) {
    passwordStrengthClass.value = 'danger';
    passwordStrengthText.value = t('auth.password-weak');
  } else if (passwordStrength.value < 60) {
    passwordStrengthClass.value = 'warning';
    passwordStrengthText.value = t('auth.password-medium');
  } else {
    passwordStrengthClass.value = 'success';
    passwordStrengthText.value = t('auth.password-strong');
  }
});

// Form validation
const validateFirstName = () => {
  if (!firstName.value) {
    formErrors.value.firstName = t('errors.required');
  } else {
    formErrors.value.firstName = '';
  }
};

const validateLastName = () => {
  if (!lastName.value) {
    formErrors.value.lastName = t('errors.required');
  } else {
    formErrors.value.lastName = '';
  }
};

const validateEmail = () => {
  if (!email.value) {
    formErrors.value.email = t('errors.required');
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.value)) {
    formErrors.value.email = t('errors.email-invalid');
  } else {
    formErrors.value.email = '';
  }
};

const validatePhoneCode = () => {
  if (!phoneCode.value) {
    formErrors.value.phoneCode = t('errors.required');
  } else if (!/^\+\d{1,4}$/.test(phoneCode.value)) {
    formErrors.value.phoneCode = t('errors.invalid-input');
  } else {
    formErrors.value.phoneCode = '';
  }
};

const validatePhoneNumber = () => {
  if (!phoneNumber.value) {
    formErrors.value.phoneNumber = t('errors.required');
  } else if (!/^\d{10,15}$/.test(phoneNumber.value.replace(/\D/g, ''))) {
    formErrors.value.phoneNumber = t('errors.invalid-input');
  } else {
    formErrors.value.phoneNumber = '';
  }
};

const validatePassword = () => {
  if (!password.value) {
    formErrors.value.password = t('errors.required');
  } else if (password.value.length < 8) {
    formErrors.value.password = t('errors.password-weak');
  } else {
    formErrors.value.password = '';
  }

  // Also validate confirm password if it's been entered
  if (passwordConfirm.value) {
    validatePasswordConfirm();
  }
};

const validatePasswordConfirm = () => {
  if (!passwordConfirm.value) {
    formErrors.value.passwordConfirm = t('errors.required');
  } else if (passwordConfirm.value !== password.value) {
    formErrors.value.passwordConfirm = t('errors.passwords-not-match');
  } else {
    formErrors.value.passwordConfirm = '';
  }
};

const validateTerms = () => {
  if (!agreeToTerms.value) {
    formErrors.value.terms = t('errors.required');
  } else {
    formErrors.value.terms = '';
  }
};

const validateForm = () => {
  validateFirstName();
  validateLastName();
  validateEmail();
  validatePhoneCode();
  validatePhoneNumber();
  validatePassword();
  validatePasswordConfirm();
  validateTerms();

  return !formErrors.value.firstName &&
         !formErrors.value.lastName &&
         !formErrors.value.email &&
         !formErrors.value.phoneCode &&
         !formErrors.value.phoneNumber &&
         !formErrors.value.password &&
         !formErrors.value.passwordConfirm &&
         !formErrors.value.terms;
};

// Form submission
const submitSignup = async () => {
  if (!validateForm()) return;

  authStore.clearError();

  const userData = {
    email: email.value,
    username: email.value,
    phone_code: phoneCode.value,
    phone_number: phoneNumber.value,
    password: password.value,
    name: firstName.value,
    last_name: lastName.value,
  };

  if (localStorage.getItem('utm_campaign')) {
    userData.utm_campaign = localStorage.getItem('utm_campaign');
  }

  const result = await authStore.register(userData);

  if (result.success) {
    router.push(signupDestination());
  }
};

// Google login
const loginWithGoogle = async () => {
  authStore.clearError();

  try {
    const result = await authStore.loginWithGoogle();

    if (result.success) {
      const redirectPath = signupDestination();
      router.push(redirectPath);
    }
  } catch (error) {
    console.error('Google login error:', error);
    // Error is already set in the store
  }
};

const loginWithApple = async () => {
  authStore.clearError();

  try {
    const result = await authStore.loginWithApple();

    if (result.success) {
      const redirectPath = signupDestination();
      router.push(redirectPath);
    }
  } catch (error) {
    console.error('Apple login error:', error);
    // Error is already set in the store
  }
};

onMounted(async () => {
  try { locationSettings.value = await publicService.getLocationSettings(); } catch { locationSettings.value = {}; }

  if (locationSettings.value.code) { // Country code
    // Find the country code in the countryCodes array
    const country = countryCodes.find(c => c.iso === locationSettings.value.code);

    if (country) {
      phoneCode.value = country.code;
    }
  }

  if (router.currentRoute.value.query.upgraded) {
    if (router.currentRoute.value.query.email) {
      email.value = router.currentRoute.value.query.email;
    }
  }
});
</script>
